// components/notifications/TestNotificationButton.jsx
import React, { useState } from 'react';
import { Bell, Smartphone, Laptop } from 'lucide-react';
import { isMobileDevice, requestNotificationPermission, showNotification } from '../../utils/notificationUtils';

const TestNotificationButton = () => {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  
  const testNotification = async () => {
    setTesting(true);
    setResult(null);
    
    // Check if device is mobile
    const isMobile = isMobileDevice();
    
    if (isMobile) {
      // For mobile, we'll show an in-app toast
      const event = new CustomEvent('showToast', {
        detail: {
          title: '🔔 Test Notification',
          message: 'This is a test notification! On mobile, notifications work best when the app is open.',
          type: 'system',
          onClick: () => console.log('Toast clicked')
        }
      });
      window.dispatchEvent(event);
      
      setResult({
        success: true,
        message: 'Test notification shown! Check the top of your screen.'
      });
      setTesting(false);
      return;
    }
    
    // For desktop, request permission first if needed
    if (Notification.permission !== 'granted') {
      const granted = await requestNotificationPermission(true);
      if (!granted) {
        setResult({
          success: false,
          message: 'Notification permission denied. Please enable in browser settings.'
        });
        setTesting(false);
        return;
      }
    }
    
    // Show desktop notification
    try {
      const notification = new Notification('🔔 Test Notification', {
        body: 'This is a test notification! You will receive real-time updates.',
        icon: '/favicon.ico',
        silent: false,
        vibrate: [200, 100, 200]
      });
      
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
      
      setTimeout(() => notification.close(), 5000);
      
      setResult({
        success: true,
        message: 'Test notification sent! Check your desktop.'
      });
    } catch (error) {
      setResult({
        success: false,
        message: 'Failed to send notification: ' + error.message
      });
    }
    
    setTesting(false);
  };
  
  return (
    <div className="space-y-3">
      <button
        onClick={testNotification}
        disabled={testing}
        className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {testing ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
            Testing...
          </>
        ) : (
          <>
            <Bell className="w-4 h-4" />
            Test Notification
          </>
        )}
      </button>
      
      {result && (
        <div className={`p-3 rounded-lg text-sm ${result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {result.message}
        </div>
      )}
      
      <div className="text-xs text-gray-500 text-center">
        {isMobileDevice() ? (
          <div className="flex items-center justify-center gap-1">
            <Smartphone className="w-3 h-3" />
            <span>Mobile: Notifications appear as toasts at the top of the screen</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-1">
            <Laptop className="w-3 h-3" />
            <span>Desktop: Notifications appear as system popups</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestNotificationButton;